/**
 * Administration Capabilities Matrix (ODS DATA MAP V1).
 *
 * This module is the single source of truth for administration and data map
 * capability authorization. It is intentionally PURE: no server-only dependencies
 * (no `@trpc/server`, no database, no Node built-ins) so it is safe to import
 * from client bundles, feature flags, audit logs, and server adapters alike.
 *
 * Capabilities represent high-level permissions expressed as stable dotted strings.
 * Each capability maps to the specific user roles authorized to perform it.
 */

export type AdministrationRole =
  | "admin"
  | "admin_manager"
  | "project_manager"
  | "sales"
  | "technician";

/**
 * Stable dotted capability values for the Administration > Data Map cockpit.
 */
export const VIEW_DATA_CATALOG = "administration.data_map.read" as const;
export const VIEW_DATA_QUALITY = "administration.data_map.quality" as const;
export const VIEW_TECHNICAL_SCHEMA = "administration.data_map.technical_schema" as const;

export type AdministrationCapability =
  | typeof VIEW_DATA_CATALOG
  | typeof VIEW_DATA_QUALITY
  | typeof VIEW_TECHNICAL_SCHEMA;

export const ADMINISTRATION_CAPABILITIES = {
  VIEW_DATA_CATALOG,
  VIEW_DATA_QUALITY,
  VIEW_TECHNICAL_SCHEMA,
} as const;

export const ALL_ADMINISTRATION_CAPABILITIES: readonly AdministrationCapability[] = [
  VIEW_DATA_CATALOG,
  VIEW_DATA_QUALITY,
  VIEW_TECHNICAL_SCHEMA,
] as const;

/**
 * Role authorization matrix (ODS DATA MAP V1):
 * - admin:          all 3 capabilities (read, quality, technical_schema)
 * - admin_manager:  all 3 capabilities (read, quality, technical_schema)
 * - project_manager: none (fail-closed)
 * - sales:           none (fail-closed)
 * - technician:      none (fail-closed)
 * - unknown roles:   none (fail-closed)
 */
export const ADMINISTRATION_CAPABILITY_ROLES: Record<AdministrationCapability, Set<AdministrationRole>> = {
  [VIEW_DATA_CATALOG]: new Set<AdministrationRole>(["admin", "admin_manager"]),
  [VIEW_DATA_QUALITY]: new Set<AdministrationRole>(["admin", "admin_manager"]),
  [VIEW_TECHNICAL_SCHEMA]: new Set<AdministrationRole>(["admin", "admin_manager"]),
};

const CAPABILITY_NAMES = new Set<string>(ALL_ADMINISTRATION_CAPABILITIES);

/**
 * Type guard: returns true when `cap` is a known AdministrationCapability string.
 * Unknown strings resolve to `false` so callers fail closed.
 */
export function isValidAdministrationCapability(cap: string): cap is AdministrationCapability {
  return CAPABILITY_NAMES.has(cap);
}

/**
 * Returns true when `role` is authorized to exercise `capability`.
 * Unknown roles or unknown capabilities resolve to `false` (fail-closed).
 * Pure: never throws.
 */
export function hasAdministrationCapability(
  role: string | null | undefined,
  capability: string | null | undefined
): boolean {
  if (!role || !capability || !isValidAdministrationCapability(capability)) {
    return false;
  }
  const allowed = ADMINISTRATION_CAPABILITY_ROLES[capability];
  return Boolean(allowed && allowed.has(role as AdministrationRole));
}
